import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import { Login, Register, ForgotPassword, ResetPassword, AdminLogin } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import DashboardLayout from "@/layouts/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import ResourcePage from "@/pages/dashboard/ResourcePage";
import RequirementWizard from "@/pages/dashboard/RequirementWizard";
import VendorMarketplace from "@/pages/dashboard/VendorMarketplace";
import Documents from "@/pages/dashboard/Documents";
import Support from "@/pages/dashboard/Support";
import Profile from "@/pages/dashboard/Profile";
import Analytics from "@/pages/dashboard/Analytics";
import Calendar from "@/pages/dashboard/Calendar";
import QuotationCompare from "@/pages/dashboard/QuotationCompare";
import { ActivityPage, NotificationsPage, TemplatesPage, ReportsPage, PartnerPerformancePage, SettingsPage } from "@/pages/dashboard/MiscPages";
import RequirementTemplates from "@/pages/dashboard/RequirementTemplates";
import EmailCenter from "@/pages/dashboard/EmailCenter";
import PartnersList from "@/pages/dashboard/PartnersList";
import PartnerDetail from "@/pages/dashboard/PartnerDetail";
import AgreementsList from "@/pages/dashboard/AgreementsList";
import AgreementDetail from "@/pages/dashboard/AgreementDetail";
import AgreementWizard from "@/pages/dashboard/AgreementWizard";
import CompanyProfile from "@/pages/dashboard/CompanyProfile";
import ClauseLibrary from "@/pages/dashboard/ClauseLibrary";
import AgreementTemplates from "@/pages/dashboard/AgreementTemplates";

import {
  PlatformPage, SolutionsPage, ClientsPage, VendorsPage,
  StaffingPartnersPage, AboutPage, ProductsPage, ResourcesPage,
} from "@/pages/PublicPages";

function Protected({ children, requireOnboarded = true }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireOnboarded && user.role !== "super_admin" && !user.onboarding_complete && user.workspace_access !== true) return <Navigate to="/onboarding" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/staffing-partners" element={<StaffingPartnersPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/secure-admin" element={<AdminLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={<Protected requireOnboarded={false}><Onboarding /></Protected>} />
          <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<Navigate to="/app/overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="compare/:rfqId" element={<QuotationCompare />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="requirement-new" element={<RequirementWizard />} />
            <Route path="marketplace" element={<VendorMarketplace />} />
            <Route path="documents" element={<Documents />} />
            <Route path="expiring-documents" element={<Documents expiring />} />
            <Route path="support" element={<Support />} />
            <Route path="profile" element={<Profile />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="partner-performance" element={<PartnerPerformancePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="partner-onboarding" element={<RequirementTemplates />} />
            <Route path="email-center" element={<EmailCenter />} />
            <Route path="partners" element={<PartnersList />} />
            <Route path="partners/:orgId" element={<PartnerDetail />} />
            <Route path="agreements" element={<AgreementsList />} />
            <Route path="agreements/new" element={<AgreementWizard />} />
            <Route path="agreements/:agreementId" element={<AgreementDetail />} />
            <Route path="company-profile" element={<CompanyProfile />} />
            <Route path="agreement-clauses" element={<ClauseLibrary />} />
            <Route path="agreement-templates" element={<AgreementTemplates />} />
            <Route path=":key" element={<ResourcePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
